import { isCalifornia } from "encamp-shared/src/constants/states";
import { KnownSubmissionDocumentTypes } from "encamp-shared/src/documents";

import type { DocumentMetadataFormData } from "../types";

const SUBMISSION_DOCUMENT_TYPES = new Set<string>(KnownSubmissionDocumentTypes);

type CurrentSubmissionFields = Pick<
  DocumentMetadataFormData,
  "offerCurrentSubmissionDocument" | "currentSubmissionDocument" | "existingCurrentSubmission"
>;

export function isSubmissionDocumentType(documentType: string | null | undefined): boolean {
  return documentType != null && SUBMISSION_DOCUMENT_TYPES.has(documentType);
}

/**
 * Offered for a document with no facilities yet, or one never reviewed and
 * linked to a single facility (an upload from that facility's page). The latter
 * takes the upload's choice if one was handed off, else shows the facility's
 * current flag without saving it. Otherwise existing per-facility flags stand.
 * A refresh of the same document keeps the form's values.
 */
export function initialCurrentSubmissionFields({
  isNewDocument,
  facilityIds,
  currentSubmissionFacilityIds,
  reviewed,
  carryForwardByDefault,
  currentValues,
}: {
  isNewDocument: boolean;
  facilityIds: string[];
  currentSubmissionFacilityIds: string[];
  reviewed: boolean;
  carryForwardByDefault: boolean | undefined;
  currentValues: CurrentSubmissionFields;
}): CurrentSubmissionFields {
  if (!isNewDocument) {
    return currentValues;
  }
  if (facilityIds.length === 0) {
    return { offerCurrentSubmissionDocument: true };
  }
  if (facilityIds.length === 1 && !reviewed) {
    return {
      offerCurrentSubmissionDocument: true,
      currentSubmissionDocument: carryForwardByDefault,
      existingCurrentSubmission: {
        facilityId: facilityIds[0],
        current: currentSubmissionFacilityIds.includes(facilityIds[0]),
      },
    };
  }
  return { offerCurrentSubmissionDocument: false };
}

type CurrentSubmissionFormData = CurrentSubmissionFields &
  Pick<DocumentMetadataFormData, "documentType" | "facilities">;

/** The already-linked facility's flag, while that facility is still the only one. */
function unchangedExistingFlag(data: CurrentSubmissionFormData): boolean | undefined {
  const existing = data.existingCurrentSubmission;
  const [onlyFacility, ...others] = data.facilities ?? [];
  if (!existing || others.length > 0 || onlyFacility?.id !== existing.facilityId) {
    return undefined;
  }
  return existing.current;
}

/**
 * The checkbox's state; undefined hides it. Until the user chooses, it shows the
 * already-linked facility's flag while the facilities are unchanged, else checked
 * only when one facility is linked, since each facility's current document of
 * that type would be replaced.
 */
export function currentSubmissionDocumentShown(
  data: CurrentSubmissionFormData
): boolean | undefined {
  if (!data.offerCurrentSubmissionDocument || !isSubmissionDocumentType(data.documentType)) {
    return undefined;
  }
  return (
    data.currentSubmissionDocument ?? unchangedExistingFlag(data) ?? data.facilities?.length === 1
  );
}

/**
 * The mutation's flag: what the checkbox shows, except that an untouched flag on
 * unchanged facilities is left as it is (undefined).
 */
export function currentSubmissionDocumentToSave(
  data: CurrentSubmissionFormData
): boolean | undefined {
  if (data.currentSubmissionDocument === undefined && unchangedExistingFlag(data) !== undefined) {
    return undefined;
  }
  return currentSubmissionDocumentShown(data);
}

export function hasNonCaliforniaFacility(
  facilities: DocumentMetadataFormData["facilities"]
): boolean {
  return (facilities ?? []).some((facility) => !isCalifornia(facility.state));
}
